import React, { useState, useMemo } from 'react';
import { FileText, Download, Search, X, File, FileSpreadsheet, Image as ImageIcon, Archive } from 'lucide-react';
import './EmployeeFiles.css';
import { employeeFiles, fileTypes, categories, departments, sortOptions } from '../data/employeeFilesData';

const EmployeeFiles = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [sortBy, setSortBy] = useState('name-asc');

  // Get file type icon
  const getFileIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'pdf':
        return <FileText size={24} />;
      case 'word':
      case 'doc':
      case 'docx':
        return <FileText size={24} />;
      case 'excel':
      case 'xls':
      case 'xlsx':
        return <FileSpreadsheet size={24} />;
      case 'csv':
        return <FileSpreadsheet size={24} />;
      case 'image':
      case 'jpg':
      case 'png':
      case 'gif':
        return <ImageIcon size={24} />;
      case 'zip':
        return <Archive size={24} />;
      default:
        return <File size={24} />;
    }
  };

  // Handle file download
  const handleDownload = (file) => {
    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = file.fileUrl;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedType('All');
    setSelectedCategory('All');
    setSelectedDepartment('All');
    setSortBy('name-asc');
  };

  // Filter and sort files
  const filteredFiles = useMemo(() => {
    let filtered = [...employeeFiles];

    // CASE-SENSITIVE search
    if (searchQuery.trim()) {
      filtered = filtered.filter(file => {
        const query = searchQuery; // Do NOT convert to lowercase - case-sensitive
        return (
          file.name.includes(query) ||
          file.description?.includes(query) ||
          file.category.includes(query) ||
          file.department.includes(query) ||
          file.keywords?.some(keyword => keyword.includes(query))
        );
      });
    }

    // Filter by type
    if (selectedType !== 'All') {
      filtered = filtered.filter(file => file.type === selectedType);
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(file => file.category === selectedCategory);
    }

    // Filter by department
    if (selectedDepartment !== 'All') {
      filtered = filtered.filter(file => file.department === selectedDepartment);
    }

    // Sort
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt));
        break;
      case 'updated':
        filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
        break;
      case 'size':
        filtered.sort((a, b) => {
          const sizeA = parseFloat(a.size);
          const sizeB = parseFloat(b.size);
          return sizeB - sizeA;
        });
        break;
      case 'type':
        filtered.sort((a, b) => a.type.localeCompare(b.type));
        break;
      case 'category':
        filtered.sort((a, b) => a.category.localeCompare(b.category));
        break;
      default:
        break;
    }

    return filtered;
  }, [searchQuery, selectedType, selectedCategory, selectedDepartment, sortBy]);

  const hasActiveFilters = searchQuery || selectedType !== 'All' || selectedCategory !== 'All' || selectedDepartment !== 'All';

  return (
    <div className="employee-files-page">
      {/* Hero Section */}
      <section className="files-hero">
        <div className="files-hero-content">
          <h1>Employee Files</h1>
          <p>Access company documents, forms, and resources</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="files-content">
        <div className="files-container">
          
          {/* Search Bar */}
          <div className="search-section">
            <div className="search-bar">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search employee files... (case-sensitive)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search files"
              />
              {searchQuery && (
                <button 
                  className="clear-search" 
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="filters-section">
            <div className="filters-grid">
              <select 
                value={selectedType} 
                onChange={(e) => setSelectedType(e.target.value)}
                aria-label="Filter by file type"
              >
                {fileTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>

              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                aria-label="Filter by category"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>

              <select 
                value={selectedDepartment} 
                onChange={(e) => setSelectedDepartment(e.target.value)}
                aria-label="Filter by department"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>

              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort files"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Results Count */}
          <div className="results-info">
            <p>{filteredFiles.length} {filteredFiles.length === 1 ? 'file' : 'files'} found</p>
          </div>

          {/* Files Table/List */}
          {filteredFiles.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="files-table-desktop">
                <table className="files-table">
                  <thead>
                    <tr>
                      <th>File</th>
                      <th>Category</th>
                      <th>Department</th>
                      <th>Type</th>
                      <th>Updated</th>
                      <th>Size</th>
                      <th>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFiles.map((file) => (
                      <tr 
                        key={file.id} 
                        className="file-row"
                        onClick={() => handleDownload(file)}
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleDownload(file)}
                      >
                        <td className="file-name-cell">
                          <div className="file-name-content">
                            <span className="file-icon">{getFileIcon(file.type)}</span>
                            <div>
                              <div className="file-name">{file.name}</div>
                              {file.description && (
                                <div className="file-description">{file.description}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{file.category}</td>
                        <td>{file.department}</td>
                        <td><span className="file-type-badge">{file.type}</span></td>
                        <td>{new Date(file.updatedAt).toLocaleDateString()}</td>
                        <td>{file.size}</td>
                        <td>
                          <button 
                            className="download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(file);
                            }}
                            aria-label={`Download ${file.name}`}
                          >
                            <Download size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="files-cards-mobile">
                {filteredFiles.map((file) => (
                  <div 
                    key={file.id} 
                    className="file-card"
                    onClick={() => handleDownload(file)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && handleDownload(file)}
                  >
                    <div className="file-card-header">
                      <span className="file-icon">{getFileIcon(file.type)}</span>
                      <span className="file-type-badge">{file.type}</span>
                    </div>
                    <h3 className="file-card-name">{file.name}</h3>
                    {file.description && (
                      <p className="file-card-description">{file.description}</p>
                    )}
                    <div className="file-card-meta">
                      <span>{file.category}</span>
                      <span>{file.department}</span>
                      <span>{file.size}</span>
                    </div>
                    <div className="file-card-footer">
                      <span>Updated: {new Date(file.updatedAt).toLocaleDateString()}</span>
                      <button 
                        className="download-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(file);
                        }}
                        aria-label={`Download ${file.name}`}
                      >
                        <Download size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <FileText size={48} />
              <h3>No files match your search</h3>
              <p>Try adjusting your search terms or filters</p>
              {hasActiveFilters && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default EmployeeFiles;
